import { Component, OnInit } from '@angular/core';
import { ReportDashboardService } from 'src/app/Services/report-dashboard.service';
import { RestService } from 'src/app/Services/rest.service';

@Component({
  selector: 'ze-admin-managment',
  templateUrl: './admin-managment.component.html',
  styleUrls: ['./admin-managment.component.scss']
})
export class AdminManagmentComponent implements OnInit {

  // Search term bound from the template (updated via input event)
  searchTerm: string = '';

  // Server-provided users (current page)
  users: Array<any> = [];

  // Dashboard admin details (may contain orgId/accountId)
  public adminDetailsReports: any = null;

  // Server pagination state
  pageSize: number = 10;
  currentStartIndex: number = 0;
  totalCount: number = 0;
  hasNext: boolean = false;

  // UI state
  isLoading: boolean = false; // page-level loader while fetching list
  isSavingUser: boolean = false; // loader while creating a user
  userCreationMessage: string = '';
  userCreationSuccess: boolean = false;

  // Cache for paginated data (key = startIndex, value = {users, hasNext, totalCount})
  private pageCache: Map<number, { users: any[]; hasNext: boolean; totalCount: number }> = new Map();

  constructor(private reportService: ReportDashboardService, private restService: RestService) { }

  ngOnInit() {
    // show loader and load first page
    this.isLoading = true;
    this.loadUsers(0);
    // Load dashboard admin details so we can prefer org/account IDs from server
    this.reportService.getDashboardCards('ALL').subscribe({
      next: (res: any) => { this.adminDetailsReports = res; },
      error: () => { /* ignore */ }
    });
  }

  // Computed filtered list based on `searchTerm` (local filter on the current page)
  get filteredUsers() {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) { return this.users; }
    return this.users.filter(u => {
      const fullName = (u.name || '').toLowerCase();
      const username = (u.userName || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return (fullName && fullName.includes(term)) || (username && username.includes(term)) || (email && email.includes(term));
    });
  }

  addUser() {
    // Open modal for adding a new user
    this.openAddModal();
  }


  // Modal state for add/edit
  modalOpen: boolean = false;
  // editingUser holds working values while modal is open (matches createUser API fields)
  // include `id` as alias for `userId` to satisfy templates that may reference either
  editingUser: { id?: string; userId?: string; userName?: string; firstName?: string; lastName?: string; contactNo?: string; emailID?: string; designation?: string; disabled?: boolean } = {};
  
  // Validation error tracking
  validationErrors: { [key: string]: string } = {};

  openAddModal() {
    this.editingUser = { userName: '', firstName: '', lastName: '', contactNo: '', emailID: '', designation: '', disabled: false };
    this.validationErrors = {};
    this.modalOpen = true;
  }

  openEditModal(user: any) {
    this.editingUser = { ...user };
    this.validationErrors = {};
    this.modalOpen = true;
  }

  // Validation helper
  private validateUser(): boolean {
    this.validationErrors = {};
    let isValid = true;

    // First Name validation
    if (!this.editingUser.firstName || !this.editingUser.firstName.trim()) {
      this.validationErrors['firstName'] = 'First Name is required';
      isValid = false;
    }

    // Last Name validation
    if (!this.editingUser.lastName || !this.editingUser.lastName.trim()) {
      this.validationErrors['lastName'] = 'Last Name is required';
      isValid = false;
    }

    // Email validation
    if (!this.editingUser.emailID || !this.editingUser.emailID.trim()) {
      this.validationErrors['emailID'] = 'Email is required';
      isValid = false;
    } else if (!this.isValidEmail(this.editingUser.emailID)) {
      this.validationErrors['emailID'] = 'Please enter a valid email address';
      isValid = false;
    }

    // Mobile validation
    if (!this.editingUser.contactNo || !this.editingUser.contactNo.trim()) {
      this.validationErrors['contactNo'] = 'Mobile number is required';
      isValid = false;
    } else if (!this.isValidPhone(this.editingUser.contactNo)) {
      this.validationErrors['contactNo'] = 'Please enter a valid mobile number (10-15 digits)';
      isValid = false;
    }

    return isValid;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidPhone(phone: string): boolean {
    const phoneRegex = /^\d{10,15}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  }

  saveUser() {
    // Validate all fields
    if (!this.validateUser()) {
      return; // Validation errors are now shown in the template
    }

    // If userId exists, update locally (server update not implemented here)
    if (this.editingUser.userId) {
      const idx = this.users.findIndex(u => u.userId === this.editingUser.userId);
      if (idx !== -1) { this.users[idx] = { ...this.users[idx], ...this.editingUser } as any; }
      this.closeModal();
      return;
    }

    // Create payload for backend. Prefer org/account values from dashboard admin details
    let orgId = this.getOrgIdForPayload();
    let accountId = this.getAccountIdForPayload();
    // If values are missing, try adminDetailsReports (populated from dashboard cards)
    if ((!orgId || !accountId) && this.adminDetailsReports) {
      orgId = orgId || this.adminDetailsReports.orgId || this.adminDetailsReports.organizationId || this.adminDetailsReports.org_id;
      accountId = accountId || this.adminDetailsReports.accountId || this.adminDetailsReports.account_id;
    }

    if (!orgId || !accountId) {
      const confirmFallback = confirm('OrgId or AccountId not found in session. Do you want to enter them now?');
      if (!confirmFallback) { return; }
      const enteredOrg = prompt('Enter orgId');
      const enteredAcct = prompt('Enter accountId');
      if (!enteredOrg || !enteredAcct) { alert('orgId and accountId are required'); return; }
      // proceed with entered values
      this.createUserOnServer(enteredOrg, enteredAcct);
    } else {
      this.createUserOnServer(orgId, accountId);
    }
  }

  private createUserOnServer(orgId: string, accountId: string) {
    this.isSavingUser = true;
    this.userCreationMessage = '';
    this.userCreationSuccess = false;
    const payload = {
      orgId: orgId,
      accountId: accountId,
      users: [
        {
          firstName: this.editingUser.firstName,
          lastName: this.editingUser.lastName,
          emailID: this.editingUser.emailID,
          contactNo: this.editingUser.contactNo,
          designation: this.editingUser.designation || ''
        }
      ]
    };

    this.restService.post('createUser', payload).subscribe({
      next: (res: any) => {
        this.isSavingUser = false;
        if (res && res.operationStatus === 'SUCCESS') {
          this.userCreationMessage = 'User created successfully';
          this.userCreationSuccess = true;
          this.closeModal();
          // Clear cache and refresh list from server so new user is visible
          this.pageCache.clear();
          this.isLoading = true;
          this.loadUsers(0, !!this.searchTerm, this.searchTerm);
        } else {
          this.userCreationMessage = 'Failed to create user: ' + (res && res.operationMessage ? res.operationMessage : 'unknown');
          this.userCreationSuccess = false;
        }
      },
      error: (err) => {
        console.error('createUser error', err);
        this.isSavingUser = false;
        this.userCreationMessage = 'Failed to create user (network error)';
        this.userCreationSuccess = false;
      }
    });
  }

  private getOrgIdForPayload(): string | null {
    // try common places: localStorage keys or token claims
    const lsOrg = localStorage.getItem('orgId') || localStorage.getItem('organizationId') || localStorage.getItem('org_id');
    if (lsOrg) { return lsOrg; }
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      if (currentUser && currentUser.token) {
        const parsed = this.parseJwt(currentUser.token);
        return parsed.orgId || parsed.org_id || parsed.org || null;
      }
    } catch (e) { }
    return null;
  }

  private getAccountIdForPayload(): string | null {
    const lsAcct = localStorage.getItem('accountId') || localStorage.getItem('account_id');
    if (lsAcct) { return lsAcct; }
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      if (currentUser && currentUser.token) {
        const parsed = this.parseJwt(currentUser.token);
        return parsed.accountId || parsed.account_id || null;
      }
    } catch (e) { }
    return null;
  }

  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) { return {}; }
  }

  closeModal() {
    this.modalOpen = false;
    this.editingUser = {};
  }

  deleteUser(user: any) {
    const display = (user.name || user.userName || ('user ' + user.userId));
    if (!confirm('Delete user "' + display + '" ?')) { return; }
    this.users = this.users.filter(u => u.userId !== user.userId);
    // no server call here (demo). If using backend delete, call API then reload page.
  }

  toggleActive(user: any, checked: boolean) {
    // API uses `disabled`; checked = true means user should be active (disabled = false)
    const userId = user.userId || user.userId;
    const previousDisabled = !!user.disabled;
    // prevent duplicate clicks
    (user as any)._updating = true;
    // optimistic UI update (immediately show change for responsiveness)
    user.disabled = !checked;

    this.restService.get('disableUser', userId).subscribe({
      next: (res: any) => {
        (user as any)._updating = false;
        if (!res || res.operationStatus !== 'SUCCESS') {
          // revert
          user.disabled = previousDisabled;
          alert('Failed to update user status.');
        } else {
          // If API returns a boolean in res.data, treat that as authoritative
          if (typeof res.data === 'boolean') {
            user.disabled = !!res.data;
          }
          // Clear cache to fetch fresh data from server reflecting the updated status
          this.pageCache.clear();
          // Refresh current page to reflect authoritative server state quickly
          this.loadUsers(this.currentStartIndex, !!this.searchTerm, this.searchTerm);
        }
      },
      error: () => {
        (user as any)._updating = false;
        user.disabled = previousDisabled;
        alert('Failed to update user status (network error).');
      }
    });
  }

  // Server-side pagination helpers
  loadUsers(startIndex: number = 0, search = false, searchParameter: string = '') {
    this.currentStartIndex = startIndex;
    // Show loader only for non-search requests (search should show results quickly)
    if (!search) {
      this.isLoading = true;
    }

    // Check if this page is already cached (only use cache if no active search)
    if (!search && this.pageCache.has(startIndex)) {
      const cached = this.pageCache.get(startIndex);
      if (cached) {
        this.users = cached.users || [];
        this.hasNext = cached.hasNext;
        this.totalCount = cached.totalCount;
      }
      this.isLoading = false;
      return;
    }

    const payload = {
      timeRange: 'ALL',
      startRange: this.currentStartIndex,
      pageSize: this.pageSize,
      search: search,
      searchParameter: searchParameter || ''
    };
    this.reportService.getUserDetails(payload).subscribe({
      next: (res: any) => {
        const data = res && res.data ? res.data : [];
        this.users = data;
        this.hasNext = !!res.hasNext;
        this.totalCount = res.totalCount || data.length;
        this.pageSize = res.pageSize || this.pageSize;
        // Store this page in cache for future navigation (including pagination metadata)
        if (!search) {
          this.pageCache.set(startIndex, {
            users: data,
            hasNext: this.hasNext,
            totalCount: this.totalCount
          });
        }
        this.isLoading = false;
      },
      error: () => {
        // handle error (keep UI stable)
        this.users = [];
        this.hasNext = false;
        this.isLoading = false;
      }
    });
  }

  prev() {
    if (this.currentStartIndex <= 0) { return; }
    const nextIndex = Math.max(0, this.currentStartIndex - this.pageSize);
    this.loadUsers(nextIndex, !!this.searchTerm, this.searchTerm);
  }

  next() {
    if (!this.hasNext) { return; }
    const nextIndex = this.currentStartIndex + this.pageSize;
    this.loadUsers(nextIndex, !!this.searchTerm, this.searchTerm);
  }

  get currentPageNumber() {
    return Math.floor(this.currentStartIndex / this.pageSize) + 1;
  }

  get totalPagesNumber() {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  onSearch(term: string) {
    this.searchTerm = term || '';
    // Clear cache when search starts to ensure fresh results
    if (this.searchTerm) {
      this.pageCache.clear();
    }
    // call API with search parameter
    this.loadUsers(0, !!this.searchTerm, this.searchTerm);
  }

}
